import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction, comparer } from 'mobx';
import { DrivesContainer, FiltersRow, ContentArea, CardViewXContainer, LoadingContainer } from './Drives.styled';
import { CircularProgress } from '@ann/web-components';
import Filters from './components/Filters/Filters';
import { useDrivesStores } from '../../hook/use-drives.hook';
import RenameModal from '../../components/RenameModal';
import { formatLastUpdated } from "../../../../../utils/dateUtils";
import DriveCard from './components/DriveCard';
import { useLoadMorePagination } from '../../hooks/useLoadMorePagination';
import { LoadMoreSection } from '../../components/LoadMoreSection';

const Drives = observer(() => {
  const { drivesViewStore } = useDrivesStores();
  const [drives, setDrives] = useState([]);
  const [selectedDrivesSet, setSelectedDrivesSet] = useState(new Set());
  const [filterText, setFilterText] = useState('');

  // Use loading state from store
  const isLoading = drivesViewStore.isLoading;

  // ✅ Sync drives from store using MobX reaction
  useEffect(() => {
    // Set initial values
    setDrives(drivesViewStore.drives || []);
    setFilterText(drivesViewStore.filterText || '');

    // Setup reaction to sync drives when they change in the store
    const disposer = reaction(
      () => drivesViewStore.drives.slice(),
      (newDrives) => {
        setDrives(newDrives);
      },
      { 
        fireImmediately: false,
        equals: comparer.structural
      }
    );

    return () => disposer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Execute only once - the reaction handles updates

  const filteredAndSortedDrives = useMemo(() => {
    let filtered = drives;

    if (filterText) {
      filtered = filtered.filter(drive =>
        drive.title.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    return filtered;
  }, [drives, filterText]);

  const enhancedDrives = useMemo(() => {
    return filteredAndSortedDrives.map(drive => ({
      ...drive,
      isSelected: selectedDrivesSet.has(drive.id),
      formattedDate: formatLastUpdated(drive.lastUpdated)
    }));
  }, [filteredAndSortedDrives, selectedDrivesSet]);

  const {
    visibleItems: visibleDrives,
    hasMore: hasMoreDrives,
    isLoading: isLoadingMoreDrives,
    loadMore: loadMoreDrives,
  } = useLoadMorePagination(
    enhancedDrives,
    10,
    2000,
    drivesViewStore.hasMoreDrives,
    () => drivesViewStore.loadMoreDrives(),
    drivesViewStore.isLoadingMoreDrives
  );

  const handleFilterChange = useCallback((text) => {
    setFilterText(text);
    drivesViewStore.setFilterText(text);
  }, [drivesViewStore]);

  const handleRenameSuccess = useCallback((driveId, newTitle) => {
    setDrives(prev => prev.map(drive =>
      drive.id === driveId ? { ...drive, title: newTitle } : drive
    ));
  }, []);

  const handlersMap = useMemo(() => {
    const map = new Map();
    
    enhancedDrives.forEach(drive => {
      const onCheckboxChange = () => {
        setSelectedDrivesSet(prev => {
          const newSet = new Set(prev);
          if (newSet.has(drive.id)) {
            newSet.delete(drive.id);
          } else {
            newSet.add(drive.id);
          }
          return newSet;
        });
      };

      const onMenuChange = (value) => {
        if (value === 'rename') {
          drivesViewStore.openRenameModal(drive.id, drive.title, handleRenameSuccess);
        }
      };

      map.set(drive.id, {
        onCheckboxChange,
        onMenuChange
      });
    });
    
    return map;
  }, [enhancedDrives, drivesViewStore, handleRenameSuccess]);

  return (
    <DrivesContainer>
      <RenameModal store={drivesViewStore.renameModalStore} />
      <FiltersRow>
        <Filters
          onFilterChange={handleFilterChange}
        />
      </FiltersRow>
      <ContentArea>
        {isLoading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <CardViewXContainer>
            {visibleDrives.map((drive) => {
              const handlers = handlersMap.get(drive.id);
              
              return (
                <DriveCard 
                  key={drive.id} 
                  drive={drive}
                  isSelected={drive.isSelected}
                  onCheckboxChange={handlers.onCheckboxChange}
                  onMenuChange={handlers.onMenuChange}
                  dateLabel={drive.formattedDate.label}
                  dateTooltip={drive.formattedDate.tooltip}
                />
              );
            })}
            <LoadMoreSection
              hasMore={hasMoreDrives}
              isLoading={isLoadingMoreDrives}
              onLoadMore={loadMoreDrives}
              skeletonType="folder"
              skeletonCount={10}
            />
          </CardViewXContainer>
        )}
      </ContentArea>
    </DrivesContainer>
  );
});

Drives.displayName = 'Drives';

export default Drives;
